/**
 * QL-375 group 6 / QL-677 — the Apply dialog, in two modes:
 *
 *   - "template" — "Create portfolio from mandate template", opened from the
 *     empty custom portfolio: a dropdown of every active template on the
 *     goal's platform (any risk score), the amount, the plan. Builds the
 *     portfolio from the picked template and NEVER sets the goal's mandate —
 *     that is the Mandate selection section's job. One step: nothing is
 *     written until "Create from mandate".
 *   - "mandate" — "Update portfolio from mandate", opened from the deviation
 *     card's footer: locked to the goal's current mandate, the amount
 *     prefilled from the portfolio's total, the holdings replaced.
 *
 * The lines show at once with their weights and blank amounts; the amount
 * typed is then previewed through the server's plan (debounced): the amounts
 * fill in, then the amber box for units-priced lines landing on whole units,
 * the min-buy box, and the red box when a line cannot be created (Notion row
 * 10: the shortfall and the created lines' shifted weights). "Apply mandate"
 * writes the plan, all lines or none.
 */

import { formatMandatePct } from "@repo/shared";
import type {
  ApplyMandateLineReason,
  ApplyMandatePreviewResponse,
  GoalMandateItem,
  MandateTemplateCandidate,
  ModelMandateMatch,
  Translated,
} from "@repo/shared/types";
import { AlertTriangle, XCircle } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { OptionRow } from "@/components/ui/option-row";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { SearchableSelect, type SearchableSelectOption } from "@/components/ui/searchable-select";
import { apiErrorMessage } from "@/lib/api/_client";
import {
  useApplyMandate,
  usePreviewApplyMandate,
} from "@/lib/hooks/use-flow-instances/use-mandate";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { useRegionalFormat } from "@/lib/regional-format/context";
import { T } from "./apply-mandate-dialog.i18n";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  instanceId: string;
  goalId: string;
  portfolioId: string;
  /** QL-677 — "template": pick any platform template; "mandate": the goal's current mandate, locked. */
  mode: "template" | "mandate";
  /** Template mode: the dropdown's templates (every active one on the goal's platform). */
  templates?: MandateTemplateCandidate[];
  /** Template mode: preselect this one (the goal's selected template, when it is in the list). */
  initialTemplateId?: string | null;
  /**
   * QL-760 — template mode: the templates matching the goal's risk score and
   * platform (the state's `templateCandidates`), listed first. Never a default
   * pick on their own: only the client's existing bank mandate or the goal's
   * selected template is preselected.
   */
  riskMatchIds?: string[];
  /** Mandate mode: the goal's mandate, for the lines shown before an amount is entered. */
  mandate: GoalMandateItem | null;
  /** The portfolio's schedule, so the amount label is right before the first preview. */
  perDeposit: boolean;
  frequency: string | null;
  /** Runs after a successful Apply (the builder's own refresh, e.g. completion). */
  onApplied?: () => void;
  /** QL-664 — "Update portfolio from mandate": start from the portfolio's current total. */
  initialAmount?: number;
  /** QL-664 — the portfolio holds products: Apply replaces them, and the copy says so. */
  replaces?: boolean;
  /**
   * QL-695 — an account follow-up: the holdings become targets and the rest
   * is sold in full; the copy says so before the first preview (the plan then
   * carries it). `agreement` reads like a new recurring portfolio.
   */
  followup?: "account" | "agreement" | null;
  /**
   * QL-730 A2 — mandate mode on a MODEL portfolio: the model portfolios that
   * match the mandate line for line, which Apply switches to (no amount, no
   * plan). Null / absent on a custom portfolio.
   */
  modelMatches?: ModelMandateMatch[] | null;
  /** QL-730 A2 — the model portfolio in use today, marked in the list. */
  currentModelPortfolioId?: string | null;
};

const PREVIEW_DEBOUNCE_MS = 400;
/** QL-695 — the template-mode entry that builds from the client's existing (bank) mandate: no templateId. */
const EXISTING_MANDATE = "__existing_mandate__";

function reasonText(
  reason: ApplyMandateLineReason | null,
  t: Translated<typeof T>,
  minBuy: string,
): string {
  switch (reason) {
    case "below_min_buy":
      return t.applyReasonBelowMinBuy(minBuy);
    case "no_isin":
      return t.applyReasonNoIsin;
    case "no_product":
      return t.applyReasonNoProduct;
    case "inactive":
      return t.applyReasonInactive;
    case "not_on_platform":
      return t.applyReasonNotOnPlatform;
    case "no_price":
      return t.applyReasonNoPrice;
    case "below_one_unit":
      return t.applyReasonBelowOneUnit;
    case "unmatched_holding":
      return t.applyReasonUnmatchedHolding;
    default:
      return "";
  }
}

const lineName = (l: { name: string | null; isin: string | null }) => l.name ?? l.isin ?? "";

export function ApplyMandateDialog({
  open,
  onOpenChange,
  instanceId,
  goalId,
  portfolioId,
  mode,
  templates = [],
  initialTemplateId = null,
  riskMatchIds = [],
  mandate,
  perDeposit,
  frequency,
  onApplied,
  initialAmount,
  replaces = false,
  followup = null,
  modelMatches = null,
  currentModelPortfolioId = null,
}: Props) {
  const t = useT(T);
  const localize = useLocalize();
  // QL-730 A2 — a model portfolio: the pick among the matches; no amount, no plan.
  const modelMode = mode === "mandate" && modelMatches != null;
  const matches = modelMode ? (modelMatches ?? []) : [];
  const [modelPortfolioId, setModelPortfolioId] = useState<string>("");
  const matchKey = matches.map((m) => m.portfolioId).join(",");
  useEffect(() => {
    if (!open || !modelMode) return;
    const ids = matchKey ? matchKey.split(",") : [];
    setModelPortfolioId(
      currentModelPortfolioId && ids.includes(currentModelPortfolioId)
        ? currentModelPortfolioId
        : (ids[0] ?? ""),
    );
  }, [open, modelMode, matchKey, currentModelPortfolioId]);
  const [amountText, setAmountText] = useState("");
  const [plan, setPlan] = useState<ApplyMandatePreviewResponse | null>(null);
  // The inputs the plan on screen was previewed for. Apply writes exactly
  // these, and waits while the field or the pick differs from them (PR 688
  // review): a template switch at an unchanged amount must not post the new
  // template under the old template's table during the preview debounce.
  const [planFor, setPlanFor] = useState<{ amount: number; templateId: string | null } | null>(
    null,
  );
  const [previewError, setPreviewError] = useState<string | null>(null);
  // QL-677 — template mode: the dropdown's pick. Picking writes nothing; it
  // only decides what the preview and Apply build from.
  const [templateId, setTemplateId] = useState<string>("");
  const preview = usePreviewApplyMandate(instanceId, portfolioId);
  const apply = useApplyMandate(instanceId, goalId, portfolioId);
  const templateMode = mode === "template";
  // QL-695 — while the goal uses the client's existing (bank) mandate, the
  // dropdown leads with it: picking it builds from the goal's mandate (no
  // templateId), exactly as the card's footer does.
  const existingOption = templateMode && !!mandate && mandate.source === "bank" ? mandate : null;
  const hasExisting = !!existingOption;
  const pickedExisting = hasExisting && templateId === EXISTING_MANDATE;
  const pickedTemplate =
    templateMode && !pickedExisting ? templates.find((c) => c.id === templateId) : undefined;
  // What Apply builds from: the picked template, the existing mandate, or the goal's mandate.
  const sourceLines = templateMode ? (pickedExisting ? mandate : pickedTemplate) : mandate;
  // The id the server gets: null = the goal's current mandate.
  const requestTemplateId = templateMode && !pickedExisting ? templateId : null;

  // The tenant's own number format, as every other amount field: "100,000"
  // and "100 000" are a hundred thousand where those are the separators, not
  // a hundred (PR 598 review). Unparseable input says so below the field.
  const { formatNumber, parseNumber } = useRegionalFormat();
  const parsedAmount = parseNumber(amountText);
  const amount = parsedAmount ?? Number.NaN;
  const amountValid = parsedAmount != null && parsedAmount > 0;
  const amountInvalid = amountText.trim() !== "" && !amountValid;

  // Reset on open; the amount is the dialog's, not the portfolio's — except
  // when the caller starts it from the portfolio's current total (QL-664),
  // which previews at once.
  useEffect(() => {
    if (!open) return;
    setAmountText(initialAmount && initialAmount > 0 ? formatNumber(initialAmount) : "");
    setPlan(null);
    setPlanFor(null);
    setPreviewError(null);
  }, [open, initialAmount, formatNumber]);

  // Template mode starts on the client's existing bank mandate while the goal
  // uses it, else on the goal's selected template when it is in the list, else
  // on nothing: the advisor picks (QL-760). The dialog never
  // remembers an earlier pick. Keyed on the ids, not the array: the prop is
  // rebuilt on every render of the caller, and an array dependency would reset
  // the dialog on each keystroke.
  const templateKey = templates.map((c) => c.id).join(",");
  useEffect(() => {
    if (!open) return;
    if (!templateMode) {
      setTemplateId("");
      return;
    }
    const ids = templateKey ? templateKey.split(",") : [];
    setTemplateId(
      hasExisting
        ? EXISTING_MANDATE
        : initialTemplateId && ids.includes(initialTemplateId)
          ? initialTemplateId
          : "",
    );
  }, [open, templateMode, templateKey, initialTemplateId, hasExisting]);

  // QL-760 — the dropdown: the existing mandate, then the risk-score matches,
  // then every other platform template, each run under its heading. Headings
  // only when there are matches to set apart.
  const riskMatchSet = new Set(riskMatchIds);
  const hasRiskMatch = templates.some((c) => riskMatchSet.has(c.id));
  const templateOptions: SearchableSelectOption[] = [
    ...(existingOption
      ? [{ value: EXISTING_MANDATE, label: t.applyExistingMandateOption(existingOption.name) }]
      : []),
    ...templates
      .filter((c) => riskMatchSet.has(c.id))
      .map((c) => ({
        value: c.id,
        label: localize(c.nameI18n),
        group: t.applyTemplateGroupMatching,
      })),
    ...templates
      .filter((c) => !riskMatchSet.has(c.id))
      .map((c) => ({
        value: c.id,
        label: localize(c.nameI18n),
        group: hasRiskMatch ? t.applyTemplateGroupOther : undefined,
      })),
  ];

  // Template mode needs a template (or the existing mandate) before anything can be previewed or applied.
  const sourceReady = !templateMode || !!pickedTemplate || pickedExisting;

  // The plan follows the amount (and the picked template), a beat behind the
  // typing. Only the latest request may set it: a slow earlier response must
  // not overwrite a newer plan.
  const previewSeq = useRef(0);
  useEffect(() => {
    if (!open || !amountValid || !sourceReady || modelMode) {
      previewSeq.current += 1;
      setPlan(null);
      setPlanFor(null);
      return;
    }
    const handle = setTimeout(() => {
      const seq = ++previewSeq.current;
      preview
        .mutateAsync({ amount, templateId: requestTemplateId })
        .then((p) => {
          if (seq !== previewSeq.current) return;
          setPlan(p);
          setPlanFor({ amount, templateId: requestTemplateId });
          setPreviewError(null);
        })
        .catch((err) => {
          if (seq !== previewSeq.current) return;
          setPlan(null);
          setPlanFor(null);
          setPreviewError(apiErrorMessage(err, t.applyPreviewError));
        });
    }, PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(handle);
    // Only the amount and the pick matter here; the mutation object is not a dependency on purpose.
  }, [open, amount, amountValid, sourceReady, requestTemplateId, modelMode]);

  const fmt = (v: number) => formatNumber(v, { maximumFractionDigits: 0 });
  const creatable = plan?.lines.filter((l) => l.status !== "cannot_create") ?? [];
  const approximate = plan?.lines.filter((l) => l.status === "approximate") ?? [];
  const belowMin = plan?.lines.filter((l) => l.status === "below_min_buy") ?? [];
  const missing = plan?.lines.filter((l) => l.status === "cannot_create") ?? [];
  // QL-695 — an account follow-up: kept lines and full sells.
  const kept = plan?.lines.filter((l) => l.status === "kept") ?? [];
  const sells = plan?.sells ?? [];
  const accountFollowup = (plan?.followup ?? followup) === "account";
  // The plan on screen must be the plan for the amount in the field AND the
  // pick in the dropdown: until the preview for both returns, Apply waits — it
  // never writes a plan the advisor has not seen (PR 598 review; the pick,
  // PR 688 review). Compared on the inputs the plan was requested with, so a
  // switch disables the button on the same render, before the debounce.
  const planIsCurrent =
    !!plan &&
    !!planFor &&
    amountValid &&
    planFor.amount === amount &&
    planFor.templateId === requestTemplateId;
  const canApply = modelMode
    ? matches.some((m) => m.portfolioId === modelPortfolioId) && !apply.isPending
    : sourceReady && planIsCurrent && creatable.length > 0 && !preview.isPending;

  // The portfolio's own schedule decides the label; the plan echoes it.
  const isPerDeposit = plan?.perDeposit ?? perDeposit;
  const freq = plan?.frequency ?? frequency;
  const amountLabel = !isPerDeposit
    ? t.applyAmountLabel
    : freq === "quarterly"
      ? t.applyAmountPerQuarter
      : freq === "yearly" || freq === "annually"
        ? t.applyAmountPerYear
        : t.applyAmountPerMonth;

  const handleApply = async () => {
    if (!canApply) return;
    if (!modelMode && !planFor) return;
    try {
      // Exactly the inputs the plan on screen was built from — or, QL-730 A2,
      // the matching model portfolio picked (the amount is ignored).
      const result = await apply.mutateAsync(
        modelMode
          ? { modelPortfolioId }
          : { amount: planFor!.amount, templateId: planFor!.templateId },
      );
      if (!result.success) {
        toast.error(t.applyError, { description: result.error });
        return;
      }
      toast.success(modelMode ? t.applyModelSwitched : t.applyApplied);
      onOpenChange(false);
      onApplied?.();
    } catch (err) {
      toast.error(t.applyError, { description: apiErrorMessage(err, t.applyError) });
    }
  };

  // The table before an amount: the mandate's own lines, weight only. With a
  // plan: the plan's lines, amounts filled in.
  type Row = {
    key: string;
    name: string;
    weightPct: number;
    amount: number | null;
    notCreated: boolean;
    muted: boolean;
    /** QL-695 — an account follow-up: the buy (+) or sell (−) the target means; null off follow-ups. */
    delta: number | null;
    kept: boolean;
  };
  // PR 701 review — a model portfolio: the SELECTED match's own lines, with
  // the amounts the service spread over them (two matches on a min/max
  // mandate hold different weights; nothing is computed here).
  const selectedMatch = modelMode
    ? (matches.find((m) => m.portfolioId === modelPortfolioId) ?? null)
    : null;
  const rows: Row[] = selectedMatch
    ? selectedMatch.lines.map((l) => ({
        key: l.isin,
        name: localize(l.nameI18n) || l.isin,
        weightPct: l.weightPct,
        amount: l.amount,
        notCreated: false,
        muted: false,
        delta: null,
        kept: false,
      }))
    : plan
      ? plan.lines.map((l) => ({
          key: l.productId ?? l.isin ?? l.name ?? "",
          name: lineName(l),
          weightPct: l.weightPct,
          amount: l.status === "cannot_create" ? null : l.amount,
          notCreated: l.status === "cannot_create",
          muted: l.status === "cannot_create",
          delta: l.delta,
          kept: l.status === "kept",
        }))
      : (sourceLines?.lines ?? []).map((l) => {
          const weightPct = (sourceLines?.mode === "fixed" ? l.weightPct : l.defaultPct) ?? 0;
          return {
            key: l.isin ?? l.name ?? String(l.order),
            name: l.name ?? l.isin ?? "",
            weightPct,
            amount: null,
            notCreated: false,
            muted: false,
            delta: null,
            kept: false,
          };
        });
  // QL-730 A2 — the model dialog's total: the portfolio's planned amount.
  const modelTotal = selectedMatch?.lines.some((l) => l.amount != null)
    ? selectedMatch.lines.reduce((sum, l) => sum + (l.amount ?? 0), 0)
    : null;
  const fmtDelta = (d: number) => (d > 0 ? `+${fmt(d)}` : d < 0 ? `−${fmt(-d)}` : "0");
  const totalWeight = plan
    ? creatable.reduce((s, l) => s + l.weightPct, 0)
    : rows.reduce((s, r) => s + r.weightPct, 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl" guardDismiss={false}>
        <DialogHeader>
          <DialogTitle>
            {modelMode
              ? t.applyTitleModel
              : templateMode
                ? t.applyTitleTemplate
                : replaces
                  ? t.applyTitleReplace
                  : t.applyTitle}
          </DialogTitle>
          <DialogDescription>
            {modelMode
              ? t.applyDescriptionModel
              : templateMode
                ? t.applyDescriptionTemplate
                : accountFollowup
                  ? t.applyDescriptionFollowup
                  : replaces
                    ? t.applyDescriptionReplace
                    : t.applyDescription}
          </DialogDescription>
        </DialogHeader>

        {modelMode ? (
          // QL-730 A2 — the matching model portfolios, one to switch to; the
          // mandate's lines show below so the advisor sees what it equals.
          <div className="space-y-1.5">
            <Label>{t.applyModelLabel}</Label>
            {matches.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t.applyModelNone}</p>
            ) : (
              <RadioGroup value={modelPortfolioId} onValueChange={setModelPortfolioId}>
                {matches.map((m) => (
                  <OptionRow key={m.portfolioId} align="start">
                    <RadioGroupItem value={m.portfolioId} id={`apply-model-${m.portfolioId}`} />
                    <Label
                      htmlFor={`apply-model-${m.portfolioId}`}
                      className="font-normal cursor-pointer"
                    >
                      {localize(m.nameI18n)}
                      <span className="block text-xs text-muted-foreground">
                        {t.applyModelRiskScore(String(m.riskScore))}
                        {m.portfolioId === currentModelPortfolioId
                          ? ` · ${t.applyModelCurrent}`
                          : ""}
                      </span>
                    </Label>
                  </OptionRow>
                ))}
              </RadioGroup>
            )}
          </div>
        ) : templateMode ? (
          // QL-677 — which template to build from: every active one on the
          // goal's platform, any risk score. Picking it writes nothing.
          <div className="space-y-1.5">
            <Label htmlFor="apply-mandate-template">{t.applyMandateLabel}</Label>
            <SearchableSelect
              id="apply-mandate-template"
              value={templateId}
              onValueChange={setTemplateId}
              options={templateOptions}
              placeholder={t.applyTemplatePlaceholder}
              searchPlaceholder={t.applyTemplateSearch}
              noResultsMessage={t.applyTemplateNoResults}
            />
          </div>
        ) : (
          // Mandate mode: the goal's mandate, named — nothing to choose.
          mandate && (
            <div className="rounded-lg bg-muted/40 px-3 py-2 text-sm">
              <span className="text-muted-foreground">{t.applyMandateLabel}: </span>
              <span className="font-medium">{mandate.name}</span>
              <span className="text-muted-foreground">
                {" · "}
                {t.applyMandateLines(String(mandate.lines.length))}
              </span>
            </div>
          )
        )}

        {!modelMode && (
          <div className="space-y-1.5">
            <Label htmlFor="apply-mandate-amount">{amountLabel}</Label>
            <Input
              id="apply-mandate-amount"
              inputMode="decimal"
              autoFocus
              value={amountText}
              onChange={(e) => setAmountText(e.target.value)}
              placeholder={formatNumber(100000, { maximumFractionDigits: 0 })}
              aria-invalid={amountInvalid || undefined}
            />
            {amountInvalid ? (
              <p role="alert" className="text-xs text-destructive">
                {t.applyAmountInvalid}
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">{t.applyAmountHint}</p>
            )}
            {previewError && (
              <p role="alert" className="text-sm text-destructive">
                {previewError}
              </p>
            )}
          </div>
        )}

        {rows.length > 0 && (
          <div className="rounded-lg border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-muted-foreground">
                <tr>
                  <th className="text-left font-normal px-3 py-2">{t.applyColInstrument}</th>
                  <th className="text-right font-normal px-3 py-2">{t.applyColWeight}</th>
                  {/* PR 701 review — a recurring portfolio's amounts are per deposit, in model mode too. */}
                  <th className="text-right font-normal px-3 py-2">
                    {isPerDeposit ? amountLabel : t.applyColAmount}
                  </th>
                  {accountFollowup && (
                    <th className="text-right font-normal px-3 py-2">{t.applyColChange}</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.key} className={`border-t ${r.muted ? "text-muted-foreground" : ""}`}>
                    <td className="px-3 py-2">{r.name}</td>
                    <td className="px-3 py-2 text-right text-muted-foreground">
                      {formatMandatePct(r.weightPct)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {r.notCreated ? (
                        <span className="text-muted-foreground">{t.applyNotCreated}</span>
                      ) : r.amount === null ? (
                        <span className="text-muted-foreground">–</span>
                      ) : (
                        <span className="font-medium">{fmt(r.amount)}</span>
                      )}
                    </td>
                    {accountFollowup && (
                      <td className="px-3 py-2 text-right text-muted-foreground">
                        {r.kept ? t.applyChangeKept : r.delta == null ? "–" : fmtDelta(r.delta)}
                      </td>
                    )}
                  </tr>
                ))}
                <tr className="border-t bg-muted/30 font-medium">
                  <td className="px-3 py-2">{t.applyTotal}</td>
                  <td className="px-3 py-2 text-right">{formatMandatePct(totalWeight)}</td>
                  <td className="px-3 py-2 text-right">
                    {plan ? (
                      fmt(plan.created)
                    ) : modelTotal != null ? (
                      fmt(modelTotal)
                    ) : (
                      <span className="text-muted-foreground">–</span>
                    )}
                  </td>
                  {accountFollowup && <td className="px-3 py-2" />}
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {kept.length > 0 && (
          <Alert variant="warning">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>
              {kept.length === 1 ? t.applyKeptTitleOne : t.applyKeptTitleMany(String(kept.length))}
            </AlertTitle>
            <AlertDescription>
              {kept.map((l) => (
                <p key={l.isin ?? l.name ?? ""}>
                  {t.applyKeptLine({
                    instrument: lineName(l),
                    amount: fmt(l.amount),
                    reason: reasonText(l.reason, t, fmt(l.minBuyAmount ?? 0)),
                  })}
                </p>
              ))}
              {plan && plan.sizedTo != null && (
                // QL-730 B7 — the other lines were sized to the kept holding so
                // the portfolio stays on the mandate's weights.
                <p>
                  {t.applyKeptCapped({
                    created: fmt(plan.created),
                    requested: fmt(plan.requested),
                  })}
                </p>
              )}
            </AlertDescription>
          </Alert>
        )}

        {sells.length > 0 && (
          <div className="rounded-lg border px-3 py-2 text-sm space-y-1">
            {/* PR 701 review — a sell-down is not a sale in full. */}
            <p className="font-medium">
              {sells.every((x) => !x.partial)
                ? t.applySoldTitle
                : sells.every((x) => x.partial)
                  ? t.applySoldDownTitle
                  : t.applySoldMixedTitle}
            </p>
            {sells.map((x) => (
              <p key={x.positionId ?? x.productId ?? x.name} className="text-muted-foreground">
                {x.partial
                  ? // QL-730 B7 — an unmatched holding the mandate names: sold down to its kept amount.
                    t.applySoldDownLine({ instrument: x.name, kept: fmt(x.keptAmount ?? 0) })
                  : t.applySoldLine({ instrument: x.name, amount: fmt(x.amount) })}
              </p>
            ))}
          </div>
        )}

        {approximate.length > 0 && (
          <Alert variant="warning">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>
              {approximate.length === 1
                ? t.applyApproxTitleOne
                : t.applyApproxTitleMany(String(approximate.length))}
            </AlertTitle>
            <AlertDescription>
              {approximate.map((l) => (
                <p key={l.isin ?? l.name ?? ""}>
                  {t.applyApproxLine({
                    instrument: lineName(l),
                    weight: formatMandatePct(l.weightPct),
                    amount: fmt(l.amount),
                    target: fmt(l.targetAmount),
                  })}
                </p>
              ))}
              {!plan?.shortfall && <p>{t.applyApproxStillInside}</p>}
              {plan && plan.overInvested > 0 && (
                <p>
                  {t.applyOverInvested({
                    created: fmt(plan.created),
                    over: fmt(plan.overInvested),
                    requested: fmt(plan.requested),
                  })}
                </p>
              )}
            </AlertDescription>
          </Alert>
        )}

        {belowMin.length > 0 && (
          <Alert variant="warning">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>{t.applyMinBuyTitle}</AlertTitle>
            <AlertDescription>
              {belowMin.map((l) => (
                <p key={l.isin ?? l.name ?? ""}>
                  {t.applyMinBuyLine({
                    instrument: lineName(l),
                    amount: fmt(l.amount),
                    min: fmt(l.minBuyAmount ?? 0),
                  })}
                </p>
              ))}
            </AlertDescription>
          </Alert>
        )}

        {plan && missing.length > 0 && (
          <Alert variant="destructive">
            <XCircle className="h-4 w-4" />
            <AlertTitle>
              {missing.length === 1
                ? t.applyShortfallTitleOne
                : t.applyShortfallTitleMany(String(missing.length))}
            </AlertTitle>
            <AlertDescription className="space-y-1">
              {missing.map((l) => (
                <p key={l.isin ?? l.name ?? ""}>
                  {lineName(l)} — {reasonText(l.reason, t, fmt(l.minBuyAmount ?? 0))}
                </p>
              ))}
              {creatable.length === 0 ? (
                <p>{t.applyNothingToCreate}</p>
              ) : plan.notInvested <= 0 ? null : (
                <p>
                  {t.applyShortfallInvested({
                    created: fmt(plan.created),
                    requested: fmt(plan.requested),
                  })}
                </p>
              )}
              {plan.breachingLines.length > 0 && (
                <>
                  <p>{t.applyShortfallBreach}</p>
                  {plan.breachingLines.map((b) => (
                    <p key={b.isin ?? b.name ?? ""}>
                      {plan.mode === "fixed"
                        ? t.applyBreachFixed({
                            instrument: lineName(b),
                            actual: formatMandatePct(b.resultingPct),
                            weight: formatMandatePct(b.weightPct),
                          })
                        : t.applyBreachBand({
                            instrument: lineName(b),
                            actual: formatMandatePct(b.resultingPct),
                            min: formatMandatePct(b.minPct),
                            max: formatMandatePct(b.maxPct),
                          })}
                    </p>
                  ))}
                </>
              )}
            </AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <DialogCancel>{t.cancel}</DialogCancel>
          <Button
            type="button"
            onClick={handleApply}
            disabled={!canApply}
            loading={apply.isPending}
          >
            {modelMode
              ? t.applyButtonSwitch
              : templateMode
                ? t.applyButtonTemplate
                : replaces
                  ? t.applyButtonUpdate
                  : t.applyButton}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
